import { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Button } from "@mui/material";
import solarIcon from "../Assets/solar.svg";
import gridIcon from "../Assets/grid.svg";
import houseIcon from "../Assets/house.svg";
import hotwaterIcon from "../Assets/hotwater.svg";
import critical from "../Assets/critical_colored.svg";
import UpDown from "./arrows/UpDown";
import GridHouse from "./arrows/GridHouse";
import BatteryHouse from "./arrows/BatteryHouse";
import GridBattery from "./arrows/GridBattery";
import SolarBattery from "./arrows/SolarBattery";
import { capitalize, isEmpty, isNil } from "lodash";
import BatteryUsage from "./BatteryUsage";
import SolarGrid from "./arrows/SolarGrid";
import HouseToEV from "./arrows/HouseToEV";
import Ev from "./Ev";
import { Add, Remove } from "@mui/icons-material";
import PriceIndicator from "../Energy/PriceIndicator";
import HouseToHotWater from "./arrows/HouseToHotWater";

const root = () => {
  return {
    display: "flex",
    justifyContent: "center",
    "& .picture-wrapper": {
      height: "400px",
      width: "360px",
      position: "relative",
    },
    "& .picture-container": {
      position: "absolute",
    },
    "& .picture-item": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
      width: "100px",
      "& img": {
        height: "50px",
      },
    },
    "& .battery": {
      top: "150px",
      left: 0,
    },
    "& .house": {
      bottom: 0,
      left: "130px",
    },
    "& .grid": {
      top: "150px",
      right: 0,
    },
    "& .solar": {
      top: 0,
      left: "130px",
    },
    "& .ev": {
      bottom: 0,
      right: 0,
    },
    "& .hotwater": {
      left: 0,
      bottom: 0,
    },
    "& .offline-message": {
      position: "absolute",
      top: "200px",
      left: "95px",
    },
    "& .connecting": {
      position: "absolute",
      top: "200px",
      left: "135px",
    },
    "& .evBoostbutton": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 1200,
      backgroundColor: "#e9e9e9",
      padding: "1px 6px",
      fontSize: "13px",
      mt: "4px",
      "& .MuiButton-endIcon": {
        ml: "4px",
      },
      "& .MuiButton-startIcon": {
        mr: "4px",
      },
      "& .MuiSvgIcon-root": {
        fontSize: "16px",
      },
    },
    "& .batteryStormbutton": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 1200,
      backgroundColor: "#e9e9e9",
      padding: "3px 6px 1px 6px",
      fontSize: "13px",
      // ml: "14px",
      mt: "4px",
      "& .MuiButton-endIcon": {
        ml: "4px",
        pb: "2px",
      },
      "& .MuiButton-startIcon": {
        mr: "4px",
        pb: "2px",
      },
      "& .MuiSvgIcon-root": {
        fontSize: "16px",
      },
    },
    "& .price-indicator-section": {
      position: "relative",
      top: "-168px",
      "& .price_import": {
        fontSize: "18px",
      },
    },
  };
};

const PowerflowItem = ({
  className,
  label,
  value,
  icon,
  children,
  showArrows,
  image = undefined,
  extraChild = undefined,
}) => {
  const renderImg = () => {
    if (image) {
      return image;
    } else {
      return <img src={icon} alt="" />;
    }
  };

  return (
    <Box className={`picture-container ${className}`}>
      <Box className={`picture-item`}>
        {renderImg()}
        <Typography variant="subtitle1" fontWeight={700} mt={0.5}>
          {label}
        </Typography>
        <Typography variant="h3" fontSize="20px" color="#231F20" lineHeight={1}>
          {value}
        </Typography>
      </Box>
      {showArrows && children}
      {extraChild && extraChild}
    </Box>
  );
};

const Powerflow = ({
  details,
  showEv = false,
  showHotWater = false,
  onClickEvBoost = undefined,
  evBoostState = {
    evBoost: "",
    state: "",
  },
  onClickBatteryStorm = undefined,
  batteryStormState = {
    batteryStorm: "",
    state: "",
  },
  planState = {
    plan: {},
    onClickPlan: () => {},
    loading: false,
    show: false,
  },
  loading = false,
}) => {
  // const [eb, setEb] = useState(0);
  const [house, setHouse] = useState(0);
  const [battery, setBattery] = useState(0);
  const [grid, setGrid] = useState(0);
  const [solar, setSolar] = useState(0);
  const [gridPresent, setGridPresent] = useState(true);
  const [offline, setOffline] = useState(true);
  const [houseBattery, setHouseBattery] = useState(null);
  const [solarToGrid, setSolarToGrid] = useState(false);
  const [solarHouse, setSolarHouse] = useState(false);
  const [gridHouse, setGridHouse] = useState(null);
  const [gridBattery, setGridBattery] = useState(null);
  const [solarBattery, setSolarBattery] = useState(false);
  const [soc, setSoc] = useState(0);
  const [ev, setEv] = useState(0);
  const [houseToEV, setHouseToEV] = useState(false);

  const [hotwater, setHotwater] = useState(0);
  const [houseToHotWater, setHouseToHotWater] = useState(false);

  const [evBoost, setEvBoostStorm] = useState("");
  const [batteryStorm, setBatteryStorm] = useState("");

  const anyNull =
    isNil(details?.solar) ||
    isNil(details?.battery) ||
    isNil(details?.grid) ||
    isNil(details?.soc);

  useEffect(() => {
    setEvBoostStorm(evBoostState?.evBoost);
  }, [evBoostState]);

  useEffect(() => {
    setBatteryStorm(batteryStormState?.batteryStorm);
  }, [batteryStormState]);

  useEffect(() => {
    var solar = Math.round(details?.solar / 100) * 100;
    var battery = Math.round(details?.battery / 100) * 100;
    var ev = showEv ? Math.round(details?.ev / 100) * 100 : 0;
    var grid =
      Math.abs(details?.grid) > 100 ? Math.round(details?.grid / 100) * 100 : 0;
    var house = grid + solar + battery - ev;

    //Adjustment for negative solar, usually caused
    const max_solar_inverter_load_for_adjustment = 200;
    if (max_solar_inverter_load_for_adjustment * -1 < solar && solar < 0) {
      house = house - solar;
      solar = 0;
    }

    // House is negative, which should not be possible, so we need to adjust one or more of the contributing values.
    const min_negative_house_load = -500;
    if (min_negative_house_load < house && house < 0) {
      if (solar > 200) {
        solar = solar - house;
      } else {
        battery = battery - house;
      }
      house = 0;
    }

    setHouse(house);
    setBattery(battery);
    setGrid(grid);
    setSolar(solar);
    setGridPresent(details?.grid_present);
    setOffline(!details?.online || loading || anyNull);
    setSoc(details?.soc);

    //EV
    setEv(ev);
    setHouseToEV(ev > 0);

    //Hot Water
    setHotwater(details?.hotwater);
    setHouseToHotWater(details?.hotwater > 0);

    // For the purpose of turning on/off arrows combine house and EV
    const house_plus_ev = house + ev;

    // Set Arrows
    // From Battery - Done
    if (house_plus_ev > 50 && battery > 50) {
      //Battery to House
      setHouseBattery("down");
    } else if (house_plus_ev < -50 && battery < -50) {
      //House to Batterty
      setHouseBattery("up");
    } else {
      setHouseBattery(null);
    }

    //Solar
    if (
      (solar + battery > house_plus_ev + 50 && grid < -50 && solar > 50) ||
      // Showing battery->grid as solar->grid instead
      (grid < -50 && battery > 50 && solar > 50)
    ) {
      //Solar to Grid - done
      setSolarToGrid(true);
    } else {
      setSolarToGrid(false);
    }

    if (
      house_plus_ev > 50 &&
      solar + battery > 50 &&
      solar > 50 &&
      // Show all solar exports as solar->grid, and only remaining solar->house
      solar * -1 <= grid
    ) {
      //Solar to House - done
      setSolarHouse(true);
    } else {
      setSolarHouse(false);
    }

    if (house_plus_ev < -50 && house_plus_ev < solar + battery) {
      //House to Grid
      setGridHouse("up");
    } else if (house_plus_ev > 50 && house_plus_ev > solar + battery && grid > 50) {
      //Grid to House
      setGridHouse("down");
    } else {
      setGridHouse(null);
    }

    if (battery < -50 && grid > 50 && Math.abs(solar) < Math.abs(battery)) {
      //Grid to Battery
      setGridBattery("up");
    } else if (grid < -50 && battery > 50 && solar * -1 > grid) {
      //Only show battery to grid if there is no solar, or solar is less than grid export
      //Battery to Grid
      setGridBattery("down");
    } else {
      setGridBattery(null);
    }

    if (battery < -50 && solar > 50) {
      //Solar to Battery
      setSolarBattery(true);
    } else {
      setSolarBattery(false);
    }
  }, [details, loading, showEv]);

  const getValue = (value) => {
    if (offline) return <Skeleton variant="rounded" width={64} height={22} />;
    return `${value ? Math.abs(value / 1000).toFixed(1) : 0} kW`;
  };

  const getLoadState = (state) => capitalize(`${state}...`);

  const getButtonIcon = (boost, state) => {
    if (!isEmpty(state)) return undefined;
    if (["boost", "storm"].includes(boost)) {
      return <Add />;
    } else if (["boosting", "reserving"].includes(boost)) {
      return <Remove />;
    }
  };

  const getStormLabel = () =>
    batteryStorm === "reserving" ? (soc > 99 ? "Reserving" : "Charging") : "Storm";

  const getEvLabel = () => {
    if (details?.ev_status === "idle") {
      return "Unplugged";
    } else if (details?.ev_status === "suspend") {
      return "Finished";
    } else {
      return getValue(ev);
    }
  };

  return (
    <Box className="powerflow" sx={() => root()}>
      <Box className="picture-wrapper">
        <PowerflowItem
          className="battery"
          label="Battery"
          value={getValue(battery)}
          image={<BatteryUsage soc={soc} offline={offline} />}
          showArrows={!offline}
        >
          {!isNil(gridBattery) && <GridBattery type={gridBattery} />}
          {!isNil(houseBattery) && <BatteryHouse type={houseBattery} />}
          {solarBattery && <SolarBattery type="down" />}
          {!isEmpty(batteryStorm) && !offline && (
            <Button
              className="batteryStormbutton"
              onClick={onClickBatteryStorm}
              startIcon={
                batteryStorm === "storm"
                  ? getButtonIcon(batteryStorm, batteryStormState?.state)
                  : batteryStorm === "reserving"
                  ? getButtonIcon(batteryStorm, batteryStormState?.state)
                  : undefined
              }
              disabled={!isEmpty(batteryStormState?.state)}
            >
              {!isEmpty(batteryStormState?.state)
                ? getLoadState(batteryStormState?.state)
                : getStormLabel()}
            </Button>
          )}
        </PowerflowItem>
        <PowerflowItem
          className="grid"
          label="Grid"
          value={getValue(grid)}
          icon={gridPresent || loading || anyNull ? gridIcon : critical}
          showArrows={!offline}
          extraChild={
            planState.show && (
              <Box className="price-indicator-section">
                <PriceIndicator
                  plan={planState?.plan}
                  onClickPlan={planState.onClickPlan}
                  loading={planState.loading || loading || anyNull || offline}
                />
              </Box>
            )
          }
        >
          {solarToGrid && <SolarGrid type="down" />}
          {!isNil(gridHouse) && <GridHouse type={gridHouse} />}
        </PowerflowItem>
        <PowerflowItem
          className="solar"
          label="Solar"
          value={getValue(solar)}
          icon={solarIcon}
          showArrows={!offline}
        >
          {solarHouse && <UpDown type="down" />}
        </PowerflowItem>
        <PowerflowItem
          className="house"
          label="House"
          value={getValue(house)}
          icon={houseIcon}
          showArrows={!offline}
        >
          {showEv && houseToEV && <HouseToEV />}
          {showHotWater && houseToHotWater && <HouseToHotWater />}
        </PowerflowItem>
        {showEv && (
          <PowerflowItem
            className="ev"
            label="EV"
            value={getEvLabel()}
            showArrows={!offline}
            image={<Ev evStatus={details?.ev_status} />}
          >
            {showEv && !isEmpty(evBoost) && !offline && (
              <Button
                className="evBoostbutton"
                onClick={onClickEvBoost}
                startIcon={
                  evBoost === "boost"
                    ? getButtonIcon(evBoost, evBoostState?.state)
                    : evBoost === "boosting"
                    ? getButtonIcon(evBoost, evBoostState?.state)
                    : undefined
                }
                disabled={!isEmpty(evBoostState?.state)}
              >
                {!isEmpty(evBoostState?.state)
                  ? getLoadState(evBoostState?.state)
                  : evBoost === "boosting"
                  ? "Boosting"
                  : "Boost"}
              </Button>
            )}
          </PowerflowItem>
        )}

        {showHotWater && (
          <PowerflowItem
            className="hotwater"
            label="Hot Water"
            value={getValue(hotwater)}
            showArrows={!offline}
            icon={hotwaterIcon}
          ></PowerflowItem>
        )}

        {(loading || anyNull) && (
          <div className="connecting">
            <Typography variant="subtitle1" fontWeight={700}>
              Connecting...
            </Typography>
          </div>
        )}
        {offline && !loading && !anyNull && (
          <div className="offline-message">
            <Typography variant="body1" fontSize="14px" fontWeight={700}>
              Elektrobank is unavailable
            </Typography>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default Powerflow;
